import { httpProxy } from "../api";
import type { AgencyProfile } from "@/types/business";

async function createPartialBusiness(type: 'product' | 'agency') {
  return await httpProxy.post("business/partial", {
    type: type
  });
}

async function updateAgencyProfile(businessId: number, profileData: AgencyProfile) {
  return await httpProxy.patch(`agency/${businessId}/profile`, profileData);
}

async function getUserBusiness(userId: number) {
  return await httpProxy.get(`users/business/${userId}`);
}

async function getBrandByBusinessId(businessId: number) {
  return await httpProxy.get(`business/${businessId}/brand`);
}

async function getBrandLimit() {
  return await httpProxy.get('users/brand-limit');
}

async function createBrand(businessId: number, brandData: any) {
  return await httpProxy.post(`business/${businessId}/brand`, brandData);
}

async function updateBrand(businessId: number, brandId: number, brandData: { name: string; landingUrl?: string }) {
  return await httpProxy.patch(`brand/${brandId}`, brandData);
}

async function deleteBrand(businessId: number, brandId: number) {
  return await httpProxy.delete(`brand/${brandId}`);
}

async function getInactiveBrandsForReactivation(businessId: number) {
  return await httpProxy.get(`business/${businessId}/inactive-brands`);
}

async function reactivateBrand(brandId: number) {
  return await httpProxy.post(`brand/${brandId}/reactivate`);
}

async function getBrandPrompts(brandId: number, status?: string) {
  const url = status ? `brand/${brandId}/prompts?status=${status}` : `brand/${brandId}/prompts`;
  return await httpProxy.get(url);
}

async function deletePrompt(promptId: number) {
  return await httpProxy.delete(`prompt/${promptId}`);
}

async function updatePromptStatus(promptId: number, status: 'active' | 'inactive') {
  return await httpProxy.put(`prompt/${promptId}/status`, { status });
}

async function createPrompt(brandId: number, promptData: { value: string; }) {
  return await httpProxy.post(`brand/${brandId}/prompts`, promptData);
}

async function initFreePrompts(brandId: number, language?: string) {
  const payload: any = {};
  if (language) {
    payload.language = language;
  }
  return await httpProxy.post(`brand/${brandId}/prompts/init-free`, payload);
}

async function initBrandAnalysis(brandId: number) {
  return await httpProxy.post(`brand/${brandId}/first-analysis`);
}

async function getBrandAnalysis(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/first-analysis`);
}

async function initRealBrandAnalysis(brandId: number) {
  return await httpProxy.post(`brand/${brandId}/init-real-analysis`);
}

async function getRealBrandAnalysis(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/real-analysis`);
}

async function checkBrandJobStatus(brandId: number, jobType: 'fetch_pages' | 'generate_prompts' | 'run_prompts') {
  return await httpProxy.post(`brand/${brandId}/check-job`, { jobType });
}

async function triggerDomainScan(brandId: number) {
  return await httpProxy.post(`brand/${brandId}/trigger-domain-scan`);
}

async function getBrandPages(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/pages`);
}

async function getBrandSources(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/sources`);
}

async function getBrandCompetitors(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/competitors`);
}

// V2 METRICS METHODS
async function getBrandCompetitorsV2(brandId: number, options?: {
  limit?: number;
  sort_by?: 'visibility' | 'mentions' | 'name';
  date_from?: string;
  date_to?: string;
  search?: string;
  tracked_only?: boolean;
}) {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.sort_by) params.append('sort_by', options.sort_by);
  if (options?.date_from) params.append('date_from', options.date_from);
  if (options?.date_to) params.append('date_to', options.date_to);
  if (options?.search) params.append('search', options.search);
  if (options?.tracked_only) params.append('tracked_only', 'true');
  
  const url = `brand/${brandId}/competitors/metrics`;
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
  
  return await httpProxy.get(fullUrl);
}

async function getBrandSourcesV2(brandId: number, options?: {
  limit?: number;
  sort_by?: 'visibility' | 'citations' | 'name';
  date_from?: string;
  date_to?: string;
  search?: string;
}) {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.sort_by) params.append('sort_by', options.sort_by);
  if (options?.date_from) params.append('date_from', options.date_from);
  if (options?.date_to) params.append('date_to', options.date_to);
  if (options?.search) params.append('search', options.search);
  
  const url = `brand/${brandId}/sources/metrics`;
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
  
  return await httpProxy.get(fullUrl);
}

async function getBrandCompetitorsTimelineV2(brandId: number, options: {
  date_from: string;
  date_to: string;
  top_n?: number;
  granularity?: 'hour' | 'day' | 'week';
  tracked_only?: boolean;
}) {
  const params = new URLSearchParams();
  params.append('date_from', options.date_from);
  params.append('date_to', options.date_to);
  if (options.top_n) params.append('top_n', options.top_n.toString());
  if (options.granularity) params.append('granularity', options.granularity);
  if (options.tracked_only) params.append('tracked_only', 'true');
  
  const url = `brand/${brandId}/competitors/timeline?${params.toString()}`;
  return await httpProxy.get(url);
}

async function getBrandSourcesTimelineV2(brandId: number, options: {
  date_from: string;
  date_to: string;
  top_n?: number;
  granularity?: 'hour' | 'day' | 'week';
}) {
  const params = new URLSearchParams();
  params.append('date_from', options.date_from);
  params.append('date_to', options.date_to);
  if (options.top_n) params.append('top_n', options.top_n.toString());
  if (options.granularity) params.append('granularity', options.granularity);
  
  const url = `brand/${brandId}/sources/timeline?${params.toString()}`;
  return await httpProxy.get(url);
}

async function getBrandSourceDetail(brandId: number, sourceId: string) {
  return await httpProxy.get(`brand/${brandId}/sources/${sourceId}`);
}

async function getBrandPresence(brandId: number, days: number = 30, promptId?: number) {
  const params = new URLSearchParams();
  params.append('days', days.toString());
  if (promptId !== undefined && promptId !== null) {
    params.append('prompt_id', promptId.toString());
  }
  
  const url = `brand/${brandId}/presence?${params.toString()}`;
  return await httpProxy.get(url);
}

async function getLastAnalysisRunDate(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/last-analysis-run`);
}

async function getPromptSuggestions(brandId: number, language?: string) {
  const payload: any = {};
  if (language) {
    payload.language = language;
  }
  return await httpProxy.post(`brand/${brandId}/prompts/suggestions`, payload);
}

async function getPromptDetails(promptId: number) {
  return await httpProxy.get(`prompt/${promptId}/details`);
}

async function updateCompetitorTracking(brandId: number, competitorId: string, isTracked: boolean) {
  return await httpProxy.put(`brand/${brandId}/competitors/${competitorId}/tracking`, {
    is_tracked: isTracked
  });
}

async function getPromptConfiguration(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/prompt-configuration`);
}

async function setPromptConfiguration(brandId: number, languageIso2: string) {
  return await httpProxy.post(`brand/${brandId}/prompt-configuration`, {
    languageIso2
  });
}

async function getPromptsCompetitorsSummary(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/prompts/competitors-summary`);
}

async function getCompetitorPromptGaps(brandId: number, options?: { tracked_only?: boolean }) {
  const params = new URLSearchParams();
  if (options?.tracked_only === false) {
    params.append('tracked_only', 'false');
  }
  const url = `brand/${brandId}/competitors/prompt-gaps`;
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
  return await httpProxy.get(fullUrl);
}

async function getCompetitorCitedPages(brandId: number, options?: { top_n?: number }) {
  const params = new URLSearchParams();
  if (options?.top_n) params.append('top_n', options.top_n.toString());
  const url = `brand/${brandId}/competitors/cited-pages`;
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
  return await httpProxy.get(fullUrl);
}

async function getLatestBlogSuggestions(brandId: number) {
  return await httpProxy.get(`brand/${brandId}/blogs-suggestions/latest`);
}

export default {
    createPartialBusiness,
    updateAgencyProfile,
    getUserBusiness,
    getBrandByBusinessId,
    getInactiveBrandsForReactivation,
    reactivateBrand,
    getBrandLimit,
    createBrand,
    updateBrand,
    deleteBrand,
    getBrandPrompts,
    deletePrompt,
    updatePromptStatus,
    createPrompt,
    initFreePrompts,
    initBrandAnalysis,
    getBrandAnalysis,
    initRealBrandAnalysis,
    getRealBrandAnalysis,
    checkBrandJobStatus,
    triggerDomainScan,
    getBrandPages,
    getBrandSources,
    getBrandCompetitors,
    // V2 Methods
    getBrandCompetitorsV2,
    getBrandSourcesV2,
    getBrandCompetitorsTimelineV2,
    getBrandSourcesTimelineV2,
    getBrandSourceDetail,
    getBrandPresence,
    getLastAnalysisRunDate,
    getPromptSuggestions,
    getPromptDetails,
    updateCompetitorTracking,
    getPromptConfiguration,
    setPromptConfiguration,
    getPromptsCompetitorsSummary,
    getCompetitorPromptGaps,
    getCompetitorCitedPages,
    getLatestBlogSuggestions,
};
